import { Modal } from 'cpool-components/general/Modal';
import { type LOAN_REQUEST_OFFER_SOURCES } from 'cpool-constants';
import type { SchemaLoanRequestResolver_GetAcceptedOffers } from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';
import { IoDocument as DocumentIcon } from 'react-icons/io5';

import { TextWithCopy } from '~/components/TextWithCopy';
import { cn } from '~/utils/cnTailwind';

const SIDEBAR_WIDTH = '522px';

type Props = {
    acceptedOffer: SchemaLoanRequestResolver_GetAcceptedOffers[number] & {
        source: typeof LOAN_REQUEST_OFFER_SOURCES.CONSORS;
    };
    isOpen: boolean;
    onClose: () => void;
};

export const ConsorsOfferInfoDialog = ({ acceptedOffer, isOpen, onClose }: Props) => {
    const { t } = useTranslation();
    const { supportingDocumentsRequired } = acceptedOffer;

    return (
        <Modal
            open={isOpen}
            onClose={onClose}
            title={t('OFFER_MANAGEMENT.OFFER_EXTRA_INFO.TITLE')}
            variant="sidebar"
            side="right"
            width={SIDEBAR_WIDTH}
        >
            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pt-4">
                <TextWithCopy
                    text={acceptedOffer.id}
                    tooltipBeforeCopyText={t('OFFER_MANAGEMENT.ICONS_TOOLTIP.COPY_OFFER_ID')}
                    tooltipAfterCopyText={t('OFFER_MANAGEMENT.COPIED_TO_CLIPBOARD')}
                    className="text-sm text-grey"
                >
                    {t('OFFER_MANAGEMENT.OFFER_EXTRA_INFO.OFFER_ID')}: {acceptedOffer.id}
                </TextWithCopy>
                <div tabIndex={0} className="text-grey font-medium">
                    {t('CONSORS_OFFER_EXTRA_INFO.SUPPORTING_DOCUMENTS_REQUIRED')}
                </div>
                <div className="bg-neutral-100 rounded-lg p-4">
                    <ul className="list-none space-y-2">
                        {supportingDocumentsRequired?.map((documentNumber) => {
                            const isUnknown =
                                // @ts-expect-error -- documentNumber is just a number
                                t(`CONSORS_SUPPORTING_DOCUMENTS.${documentNumber}`) ===
                                `CONSORS_SUPPORTING_DOCUMENTS.${documentNumber}`;
                            return (
                                <li className="flex items-center" key={documentNumber}>
                                    <span className="mr-2 text-blue">
                                        <DocumentIcon className="w-5 h-5" />
                                    </span>
                                    <span className={cn('font-medium', isUnknown ? 'text-red-500' : 'text-grey')}>
                                        {t(
                                            // @ts-expect-error -- documentNumber is just a number
                                            `CONSORS_SUPPORTING_DOCUMENTS.${
                                                isUnknown ? 'NOT_RECOGNIZED' : documentNumber
                                            }`,
                                            {
                                                documentNumber,
                                            },
                                        )}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>
        </Modal>
    );
};
